import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  RollFormat,
  TimesheetRange,
  useLegacyRollConversionLazyQuery,
  Week,
  WeekRollFieldsFragment,
} from "../../../../../generated/graphql";
import { isDisabledDate } from "../../../../../util/date";
import TimePickerAutoAccept from "../../../Time/TimePickerAutoAccept";
import {
  formatDuration,
  getSessionDurationOptions,
  ROLL_DATE_FORMAT,
  ROLL_TIME_FORMAT,
} from "./rollUtils";
import useRollMutations from "./useRollMutations";

const { Paragraph, Text } = Typography;

const MINUTES_IN_DAY = 24 * 60;

const SessionRowContainer = styled.div`
  margin-bottom: 12px;
`;

const Preview = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
`;

export interface RollAttendance {
  id: number;
  name: string;
  isDeleted: boolean;
}

export interface RollClassInfo {
  /** HH:mm */
  startTime: string;
  durationMinutes: number;
  day: string;
}

interface SessionRow {
  rowId: number;
  key?: string;
  date: Moment | null;
  startTime: Moment;
  durationMinutes: number;
}

let nextRowId = 0;

const getDefaultDate = (
  classInfo: RollClassInfo,
  timesheetRange?: TimesheetRange
): Moment | null => {
  if (!timesheetRange) {
    return null;
  }
  const end = moment(timesheetRange.end).startOf("day");
  for (
    const date = moment(timesheetRange.start).startOf("day");
    !date.isAfter(end);
    date.add(1, "day")
  ) {
    if (date.clone().locale("en").format("dddd") === classInfo.day) {
      return date;
    }
  }
  return moment(timesheetRange.start).startOf("day");
};

/**
 * Edits the sessions (dates and times) a class ran in a week. For weeks recorded in the old
 * format, shows the sessions converted from the old format for review before switching.
 */
const WeekSessionsEditor: React.FC<{
  isVisible: boolean;
  onClose: () => void;
  classId: number;
  week: Week;
  weekRoll: WeekRollFieldsFragment;
  attendances: RollAttendance[];
  classInfo: RollClassInfo;
  timesheetRange?: TimesheetRange;
}> = ({
  isVisible,
  onClose,
  classId,
  week,
  weekRoll,
  attendances,
  classInfo,
  timesheetRange,
}) => {
  const isConversion = weekRoll.format === RollFormat.Legacy;
  const [getConversion, conversionResult] = useLegacyRollConversionLazyQuery({
    fetchPolicy: "network-only",
  });
  const conversion =
    conversionResult.data?.getClasses[0]?.legacyRollConversion ?? undefined;
  const sourceRoll = isConversion ? conversion : weekRoll;

  const { saveWeekSessions } = useRollMutations();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const isInitialised = useRef(false);

  useEffect(() => {
    if (isVisible && isConversion) {
      getConversion({ variables: { classId, week } });
    }
    if (!isVisible) {
      isInitialised.current = false;
    }
  }, [isVisible]);

  // Only initialise the rows when opening, so changes elsewhere don't reset them while editing.
  useEffect(() => {
    if (isVisible && sourceRoll && !isInitialised.current) {
      isInitialised.current = true;
      setRows(
        sourceRoll.sessions.map((session) => ({
          date: moment(session.date, ROLL_DATE_FORMAT),
          durationMinutes: session.durationMinutes,
          key: session.key,
          rowId: nextRowId++,
          startTime: moment(session.startTime, ROLL_TIME_FORMAT),
        }))
      );
      setIsAcknowledged(false);
      setError(undefined);
    }
  }, [isVisible, sourceRoll]);

  const updateRow = (rowId: number, changes: Partial<SessionRow>) =>
    setRows((rows) =>
      rows.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row))
    );

  const addRow = () =>
    setRows((rows) => [
      ...rows,
      {
        date:
          rows[rows.length - 1]?.date?.clone() ??
          getDefaultDate(classInfo, timesheetRange),
        durationMinutes: classInfo.durationMinutes,
        rowId: nextRowId++,
        startTime: moment(classInfo.startTime, ROLL_TIME_FORMAT),
      },
    ]);

  const getSessionEntries = (key?: string) =>
    key
      ? sourceRoll?.entries.filter((entry) => entry.sessionKey === key) ?? []
      : [];

  const getAttendanceName = (attendanceId: number) =>
    attendances.find((attendance) => attendance.id === attendanceId)?.name ??
    "Unknown student";

  const validate = (): string | undefined => {
    if (rows.some((row) => !row.date)) {
      return "Please select the date of every session.";
    }
    const sessionTimes = rows.map(
      (row) =>
        `${row.date!.format(ROLL_DATE_FORMAT)} ${row.startTime.format(
          ROLL_TIME_FORMAT
        )}`
    );
    if (new Set(sessionTimes).size !== sessionTimes.length) {
      return "Two sessions cannot start at the same date and time.";
    }
    if (
      rows.some(
        (row) =>
          row.startTime.hours() * 60 +
            row.startTime.minutes() +
            row.durationMinutes >
          MINUTES_IN_DAY
      )
    ) {
      return "Sessions cannot finish after midnight.";
    }
    if (conversion && conversion.warnings.length > 0 && !isAcknowledged) {
      return "Please confirm you have reviewed the issues found in the old attendance records.";
    }
    return undefined;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      await saveWeekSessions({
        acknowledgeWarnings: isAcknowledged,
        classId,
        expectedVersion: weekRoll.version ?? null,
        fingerprint: conversion?.fingerprint ?? undefined,
        sessions: rows.map((row) => ({
          date: row.date!.format(ROLL_DATE_FORMAT),
          durationMinutes: row.durationMinutes,
          key: row.key,
          startTime: row.startTime.format(ROLL_TIME_FORMAT),
        })),
        week,
      });
      message.success("Sessions saved");
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const durationOptions = getSessionDurationOptions(classInfo.durationMinutes);
  const isLoading = isConversion && conversionResult.loading;

  return (
    <Modal
      visible={isVisible}
      title={
        isConversion
          ? "Switch this week to the new roll"
          : "Class sessions this week"
      }
      okText={isConversion ? "Switch to new roll" : "Save"}
      onOk={save}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: isLoading || !sourceRoll }}
      onCancel={onClose}
      width={650}
    >
      {isConversion ? (
        <Paragraph>
          The attendance for this week was recorded in the old format. Below are
          the sessions worked out from it, please check them before switching.
          Once switched, the roll is marked by ticking which students attended
          each session.
        </Paragraph>
      ) : (
        <Paragraph>
          Enter each time the class ran this week. Students can then be marked
          as attended for each session.
        </Paragraph>
      )}
      {conversionResult.error && (
        <Alert type="error" showIcon message={conversionResult.error.message} />
      )}
      {isLoading && <Spin />}
      {conversion && conversion.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Please review the following"
          description={
            <>
              <ul>
                {conversion.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
              <Checkbox
                checked={isAcknowledged}
                onChange={(e) => setIsAcknowledged(e.target.checked)}
              >
                I have reviewed these issues
              </Checkbox>
            </>
          }
        />
      )}
      {sourceRoll && (
        <>
          {rows.length === 0 && (
            <Paragraph>
              <Text type="secondary">
                No sessions - the class did not run this week.
              </Text>
            </Paragraph>
          )}
          {rows.map((row) => {
            const entries = getSessionEntries(row.key);
            const presentNames = entries
              .filter((entry) => entry.isPresent)
              .map((entry) => getAttendanceName(entry.attendanceId));
            const commentCount = entries.filter(
              (entry) => entry.comment
            ).length;
            const deleteButton = (
              <Button
                icon={<DeleteOutlined />}
                onClick={
                  entries.length === 0
                    ? () =>
                        setRows((rows) =>
                          rows.filter(({ rowId }) => rowId !== row.rowId)
                        )
                    : undefined
                }
              />
            );

            return (
              <SessionRowContainer key={row.rowId}>
                <Space wrap>
                  <DatePicker
                    value={row.date}
                    format="ddd DD/MM/YYYY"
                    allowClear={false}
                    disabledDate={(date) =>
                      timesheetRange
                        ? isDisabledDate(date, timesheetRange)
                        : false
                    }
                    onChange={(date) => updateRow(row.rowId, { date })}
                  />
                  <TimePickerAutoAccept
                    value={row.startTime}
                    format="h:mm a"
                    use12Hours
                    minuteStep={5}
                    allowClear={false}
                    onChange={(startTime) =>
                      startTime && updateRow(row.rowId, { startTime })
                    }
                  />
                  <Select
                    value={row.durationMinutes}
                    style={{ width: 190 }}
                    onChange={(durationMinutes: number) =>
                      updateRow(row.rowId, { durationMinutes })
                    }
                  >
                    {durationOptions.map((duration) => (
                      <Select.Option key={duration} value={duration}>
                        {formatDuration(duration)} (until{" "}
                        {row.startTime
                          .clone()
                          .add(duration, "minutes")
                          .format("h:mm a")}
                        )
                      </Select.Option>
                    ))}
                  </Select>
                  {entries.length > 0 ? (
                    <Popconfirm
                      title="Students have been marked for this session. Removing the session will remove their attendance for it. Are you sure?"
                      okButtonProps={{ danger: true }}
                      onConfirm={() =>
                        setRows((rows) =>
                          rows.filter(({ rowId }) => rowId !== row.rowId)
                        )
                      }
                    >
                      {deleteButton}
                    </Popconfirm>
                  ) : (
                    deleteButton
                  )}
                </Space>
                {entries.length > 0 && (
                  <Preview>
                    {presentNames.length > 0
                      ? `Attended: ${presentNames.join(", ")}`
                      : "Nobody attended"}
                    {commentCount > 0 &&
                      ` (${commentCount} comment${
                        commentCount > 1 ? "s" : ""
                      })`}
                  </Preview>
                )}
              </SessionRowContainer>
            );
          })}
          <Button icon={<PlusOutlined />} onClick={addRow}>
            Add session
          </Button>
        </>
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginTop: 12 }}
        />
      )}
    </Modal>
  );
};

export default WeekSessionsEditor;
